@if (isOpen()) {
  <div class="fixed inset-0 z-50">
    <button
      type="button"
      class="absolute inset-0 backdrop-blur-[2px]"
      aria-label="Chiudi finestra"
      [disabled]="isLoading()"
      (click)="requestClose()"
      (keydown.enter)="requestClose()"
      (keydown.space)="requestClose()"
    ></button>
    <div class="absolute inset-0 flex items-center justify-center p-4">
      <div
        class="relative flex flex-col w-full max-w-3xl max-h-[95vh] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
        role="dialog"
        aria-modal="true"
        [attr.aria-labelledby]="'notification-dialog-title'"
        [attr.aria-describedby]="
          description() || descriptionItems().length ? 'notification-dialog-desc' : null
        "
      >
        <div
          class="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-gray-800 shrink-0"
        >
          <div class="min-w-0 flex-1">
            <h3
              id="notification-dialog-title"
              class="text-lg font-semibold text-gray-900 dark:text-white"
            >
              {{ title() }}
            </h3>
            @if (description() || descriptionItems().length) {
              <div
                id="notification-dialog-desc"
                class="mt-1 text-sm text-gray-500 dark:text-gray-400"
              >
                @if (description()) {
                  <p>{{ description() }}</p>
                }
                @if (descriptionItems().length) {
                  <ul class="mt-2 list-disc space-y-1 pl-5">
                    @for (item of descriptionItems(); track item) {
                      <li>{{ item }}</li>
                    }
                  </ul>
                }
              </div>
            }
          </div>
          <button
            type="button"
            (click)="requestClose()"
            [disabled]="isLoading()"
            class="inline-flex h-9 w-9 flex-none items-center justify-center cursor-pointer rounded-full border border-gray-200 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:border-gray-700 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-300"
            aria-label="Chiudi finestra"
          >
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form
          [formGroup]="notificationForm"
          (ngSubmit)="submitNotification()"
          class="flex flex-col flex-1 overflow-hidden"
        >
          <div class="space-y-4 px-6 py-4 overflow-y-auto flex-1">
            @if (showCategories()) {
              <div>
                <label
                  for="notification-dialog-category"
                  class="text-sm font-semibold text-gray-700 dark:text-gray-200"
                >
                  Categoria <span class="text-red-500">*</span>
                </label>
                <div class="mt-2">
                  <app-select
                    [inputId]="'notification-dialog-category'"
                    [formControl]="categoryControl"
                    [options]="categoryOptions()"
                    placeholder="Seleziona una categoria"
                  />
                </div>
                @if (categoryControl.touched && categoryControl.invalid) {
                  <p class="form-error">La categoria è obbligatoria.</p>
                }
              </div>
            }

            <div>
              <label
                for="notification-dialog-subject"
                class="text-sm font-semibold text-gray-700 dark:text-gray-200"
              >
                Oggetto <span class="text-red-500">*</span>
              </label>
              <div class="mt-2">
                <input
                  id="notification-dialog-subject"
                  type="text"
                  formControlName="subject"
                  placeholder="Esempio: Finestra di manutenzione"
                  class="h-11 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm text-gray-700 shadow-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:focus:border-blue-500"
                />
              </div>
              <div
                class="mt-2 flex items-center justify-between text-[12px] text-gray-500 dark:text-gray-400"
              ></div>
              @if (subjectControl.touched && subjectControl.invalid) {
                <p class="form-error">L'oggetto è obbligatorio.</p>
              }
            </div>

            <div>
              <label
                for="notification-dialog-message"
                class="text-sm font-semibold text-gray-700 dark:text-gray-200"
              >
                Messaggio <span class="text-red-500">*</span>
              </label>
              <div class="mt-2">
                <quill-editor
                  id="notification-dialog-message"
                  formControlName="message"
                  [readOnly]="isLoading()"
                  [modules]="messageEditorModules"
                  format="html"
                  [trimOnValidation]="true"
                  [placeholder]="'Scrivi un messaggio breve e facile da leggere.'"
                  class="notification-quill block rounded-xl border border-gray-200 bg-white text-sm text-gray-700 shadow-sm transition-all focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:focus-within:border-blue-500"
                ></quill-editor>
              </div>
              <div
                class="mt-2 flex items-center justify-between text-[12px] text-gray-500 dark:text-gray-400"
              ></div>
              @if (messageControl.touched && messageControl.invalid) {
                @if (messageControl.hasError('required')) {
                  <p class="form-error">Il messaggio è obbligatorio.</p>
                }
                @if (messageControl.hasError('maxlength')) {
                  <p class="form-error">
                    Il messaggio deve essere lungo al massimo 1000 caratteri.
                  </p>
                }
              }
            </div>

            @if (showDateRange()) {
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    for="notification-dialog-valid-from"
                    class="text-sm font-semibold text-gray-700 dark:text-gray-200"
                  >
                    Valida dal
                  </label>
                  <div class="mt-2">
                    <input
                      id="notification-dialog-valid-from"
                      type="text"
                      formControlName="validFrom"
                      [maskito]="dateMask"
                      placeholder="gg/mm/aaaa"
                      class="h-11 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm text-gray-700 shadow-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:focus:border-blue-500"
                    />
                  </div>
                  @if (validFromControl.touched && validFromControl.invalid) {
                    <p class="form-error">La data di inizio è obbligatoria.</p>
                  }
                </div>

                <div>
                  <label
                    for="notification-dialog-valid-to"
                    class="text-sm font-semibold text-gray-700 dark:text-gray-200"
                  >
                    Valida fino al
                  </label>
                  <div class="mt-2">
                    <input
                      id="notification-dialog-valid-to"
                      type="text"
                      formControlName="validTo"
                      [maskito]="dateMask"
                      placeholder="gg/mm/aaaa"
                      class="h-11 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm text-gray-700 shadow-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:focus:border-blue-500"
                    />
                  </div>
                  @if (validToControl.touched && validToControl.invalid) {
                    <p class="form-error">La data di fine è obbligatoria.</p>
                  }
                  @if (notificationForm.hasError('dateRangeInvalid') && validToControl.touched) {
                    <p class="form-error">
                      La data di fine deve essere successiva alla data di inizio.
                    </p>
                  }
                </div>
              </div>
            }
          </div>

          <div
            class="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 px-6 py-3 dark:border-gray-800 shrink-0"
          >
            <button
              type="button"
              (click)="requestClose()"
              [disabled]="isLoading()"
              class="inline-flex h-10 cursor-pointer items-center justify-center rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-600 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              {{ cancelLabel() }}
            </button>
            <button
              type="submit"
              [disabled]="notificationForm.invalid || isLoading()"
              class="inline-flex h-10 cursor-pointer items-center justify-center rounded-xl border border-blue-600 bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 dark:border-blue-500 dark:bg-blue-600 dark:hover:bg-blue-700"
            >
              {{ submitLabel() }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
}
